import React, { useState } from "react";
import { Link } from "react-router-dom";

// ─────────────────────────────────────────────
//  React.memo — Preventing Re-renders
//  Chapter 8 · Performance
//  Author: Ajao Hazzan · Full-Stack Software Engineer
// ─────────────────────────────────────────────

const PRIMARY = "#61DAFB";
const BG = "https://images.unsplash.com/photo-1555066931-4365d14bab8c?w=1600&q=80";

/* ── Code snippets stored as template literals ── */
const CODE_1 = `import { memo, useState } from 'react';

// ✅ Memoized — only re-renders if its props change
const ProductCard = memo(function ProductCard({ product, onAddToCart }) {
  console.log(\`ProductCard \${product.id} rendered\`);

  return (
    <div>
      <h3>{product.name}</h3>
      <p>\${product.price}</p>
      <button onClick={() => onAddToCart(product)}>Add to Cart</button>
    </div>
  );
});

// Without memo — ProductCard re-renders on every parent update
// With memo — ProductCard only re-renders when product or onAddToCart changes

function Shop() {
  const [cart, setCart]     = useState([]);
  const [search, setSearch] = useState('');  // Changing this won't re-render ProductCards

  // ✅ useCallback keeps the function reference stable
  const handleAddToCart = useCallback((product) => {
    setCart(prev => [...prev, product]);
  }, []);   // Stable — never recreated

  const products = useProducts(search);  // Custom hook

  return (
    <div>
      <input value={search} onChange={e => setSearch(e.target.value)} />
      <p>Cart: {cart.length}</p>
      {products.map(p => (
        <ProductCard
          key={p.id}
          product={p}
          onAddToCart={handleAddToCart}
        />
      ))}
    </div>
  );
}`;

const CODE_2 = `// Custom comparison — re-render only if id or price changes
const ProductCard = memo(
  function ProductCard({ product, onAddToCart }) { /* ... */ },
  (prevProps, nextProps) => {
    return (
      prevProps.product.id    === nextProps.product.id &&
      prevProps.product.price === nextProps.product.price &&
      prevProps.onAddToCart   === nextProps.onAddToCart
    );
    // Return true = props are equal = DON'T re-render
    // Return false = props changed = DO re-render
  }
);`;


/* ── Styles ── */
const styles = {
  page:    { minHeight: "100vh", backgroundImage: `linear-gradient(rgba(10,12,30,0.94),rgba(10,12,30,0.97)),url(${BG})`, backgroundSize: "cover", backgroundAttachment: "fixed", color: "#fff", fontFamily: "'Segoe UI',sans-serif", paddingBottom: "60px" },
  header:  { background: "rgba(10,12,30,0.95)", borderBottom: "1px solid rgba(97,218,251,0.15)", padding: "18px 32px", display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: "12px", position: "sticky", top: 0, zIndex: 100 },
  badge:   { background: "rgba(97,218,251,0.12)", border: "1px solid rgba(97,218,251,0.3)", color: PRIMARY, padding: "4px 14px", borderRadius: "999px", fontSize: "0.75rem", fontWeight: 700, letterSpacing: "0.06em" },
  backBtn: { background: "rgba(97,218,251,0.1)", border: "1px solid rgba(97,218,251,0.25)", color: PRIMARY, padding: "8px 18px", borderRadius: "999px", cursor: "pointer", fontSize: "0.82rem", fontWeight: 600, textDecoration: "none", display: "inline-flex", alignItems: "center", gap: "6px" },
  hero:    { padding: "48px 32px 24px", maxWidth: "900px", margin: "0 auto" },
  title:   { fontSize: "clamp(1.6rem,4vw,2.4rem)", fontWeight: 800, color: PRIMARY, marginBottom: "10px", lineHeight: 1.2 },
  desc:    { color: "rgba(255,255,255,0.55)", fontSize: "1rem", lineHeight: 1.7, maxWidth: "680px", marginBottom: "32px" },
  content: { maxWidth: "900px", margin: "0 auto", padding: "0 32px 40px" },
  section: { marginBottom: "36px" },
  sh:      { fontSize: "1.1rem", fontWeight: 700, color: "#fff", marginBottom: "10px", borderLeft: "3px solid " + PRIMARY, paddingLeft: "12px" },
  body:    { color: "rgba(255,255,255,0.72)", lineHeight: 1.85, fontSize: "0.95rem", marginBottom: "6px" },
  pre:     { background: "#0D0F1A", border: "1px solid rgba(97,218,251,0.15)", borderLeft: "3px solid " + PRIMARY, borderRadius: "10px", padding: "20px 22px", overflowX: "auto", margin: "14px 0", fontSize: "0.83rem", lineHeight: 1.7, color: "#CDD6F4", fontFamily: "Courier New,monospace", whiteSpace: "pre" },
  qBox:    { background: "rgba(97,218,251,0.04)", border: "1px solid rgba(97,218,251,0.12)", borderRadius: "10px", padding: "18px 22px", marginTop: "8px" },
  qTitle:  { color: PRIMARY, fontWeight: 700, fontSize: "0.82rem", letterSpacing: "0.08em", marginBottom: "10px" },
  qItem:   { color: "rgba(255,255,255,0.65)", lineHeight: 1.8, fontSize: "0.92rem", marginBottom: "4px" },
  footer:  { maxWidth: "900px", margin: "0 auto", padding: "24px 32px 0", borderTop: "1px solid rgba(255,255,255,0.07)", display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: "12px" },
  credit:  { color: "rgba(255,255,255,0.25)", fontSize: "0.75rem" },
};

/* ── Component ── */
export default function ReactMemoPreventingRerenders() {
  const [copied, setCopied] = useState(null);

  function copyCode(codeStr, idx) {
    navigator.clipboard?.writeText(codeStr).then(() => {
      setCopied(idx);
      setTimeout(() => setCopied(null), 2000);
    });
  }

  return (
    <div style={styles.page}>

      {/* Header */}
      <header style={styles.header}>
        <div style={{ display: "flex", alignItems: "center", gap: "16px", flexWrap: "wrap" }}>
          <Link to="/react-dashboard" style={styles.backBtn}>← Dashboard</Link>
          <span style={styles.badge}>Chapter 8 · Performance</span>
        </div>
        <span style={{ color: "rgba(255,255,255,0.3)", fontSize: "0.78rem" }}>
          By <strong style={{ color: PRIMARY }}>Ajao Hazzan</strong> · Full-Stack Software Engineer
        </span>
      </header>

      {/* Hero */}
      <div style={styles.hero}>
        <h1 style={styles.title}>React.memo — Preventing Re-renders</h1>
        <p style={styles.desc}>Prevent expensive child components from re-rendering unnecessarily with React.memo.</p>
      </div>

      {/* Content */}
      <div style={styles.content}>

      /* ── How Re-renders Work ── */
      <div style={styles.section}>
        <h2 style={styles.sh}>How Re-renders Work</h2>
        <p style={styles.body} dangerouslySetInnerHTML={{ __html: "By default, when a parent component re-renders, <b>all its children re-render too</b>, even if their props haven&#39;t changed. For most components this is fine. But for expensive components (large lists, heavy calculations, complex animations), unnecessary re-renders cause visible lag." }} />
      </div>

      /* ── React.memo ── */
      <div style={styles.section}>
        <h2 style={styles.sh}>React.memo</h2>
        <p style={styles.body} dangerouslySetInnerHTML={{ __html: "React.memo wraps a component and memoizes it. It only re-renders if its props change (shallow comparison). Think of it as shouldComponentUpdate for functional components." }} />
        <div style={{ position: 'relative' }}>
          <button
            onClick={() => copyCode(CODE_1, 0)}
            style={{
              position: 'absolute', top: '10px', right: '10px',
              background: 'rgba(97,218,251,0.15)',
              border: '1px solid rgba(97,218,251,0.3)',
              color: PRIMARY, borderRadius: '6px',
              padding: '4px 10px', cursor: 'pointer',
              fontSize: '0.72rem', fontWeight: 700,
            }}
          >
            {copied === 0 ? '✓ Copied' : 'Copy'}
          </button>
          <pre style={styles.pre}>{String(CODE_1)}</pre>
        </div>
      </div>

      /* ── Custom Comparison Function ── */
      <div style={styles.section}>
        <h2 style={styles.sh}>Custom Comparison Function</h2>
        <p style={styles.body} dangerouslySetInnerHTML={{ __html: "For complex props, pass a custom comparison function as the second argument to memo." }} />
        <div style={{ position: 'relative' }}>
          <button
            onClick={() => copyCode(CODE_2, 1)}
            style={{
              position: 'absolute', top: '10px', right: '10px',
              background: 'rgba(97,218,251,0.15)',
              border: '1px solid rgba(97,218,251,0.3)',
              color: PRIMARY, borderRadius: '6px',
              padding: '4px 10px', cursor: 'pointer',
              fontSize: '0.72rem', fontWeight: 700,
            }}
          >
            {copied === 1 ? '✓ Copied' : 'Copy'}
          </button>
          <pre style={styles.pre}>{String(CODE_2)}</pre>
        </div>
      </div>

      /* ── Practice Questions ── */
      <div style={styles.section}>
        <h2 style={styles.sh}>Practice Questions</h2>
        <div style={styles.qBox}>
          <div style={styles.qTitle}>📝 PRACTICE QUESTIONS</div>
          <div style={styles.qItem}>✦ 1. Does React.memo do a deep or shallow comparison of props?</div>
          <div style={styles.qItem}>✦ 2. When does React.memo NOT help performance?</div>
          <div style={styles.qItem}>✦ 3. What happens if you pass a new object literal as a prop to a memoized component?</div>
          <div style={styles.qItem}>✦ 4. Should you wrap every component with React.memo?</div>
          <div style={styles.qItem}>✦ 5. Profile a large list component with React DevTools and add memo to fix the issue.</div>
        </div>
      </div>

        {/* Footer */}
        <div style={styles.footer}>
          <Link to="/react-dashboard" style={styles.backBtn}>← Back to React Dashboard</Link>
          <span style={styles.credit}>Mastering React · © 2024 Ajao Hazzan</span>
        </div>
      </div>

    </div>
  );
}
